import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Calendar as CalendarIcon, Image, Users, Home as HomeIcon, Music, ShoppingBag, ChevronDown, UserPlus, MapPin, LogIn, LogOut, Shield, Star, Coins, User, Bike, Lock, Bot, Share2, UtensilsCrossed, Eye, EyeOff, Edit, Save, Trash2, Plus, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const ICON_MAP = {
  HomeIcon, CalendarIcon, Image, Users, Music, ShoppingBag, ChevronDown, UserPlus,
  MapPin, Shield, Coins, User, Bike, Bot, UtensilsCrossed, Star, LogIn, LogOut, Share2, ExternalLink,
};
const resolveIcon = (name) => ICON_MAP[name] || ChevronDown;


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { member, isAdmin, isMember, logout, getToken, realIsModerator, viewAsUser, toggleViewAsUser } = useAuth();
  const [notifCounts, setNotifCounts] = useState({ unanswered_messages: 0, pending_bookings: 0, total: 0 });
  const [menuItems, setMenuItems] = useState([]);
  const [menuEditorOpen, setMenuEditorOpen] = useState(false);
  const [menuForm, setMenuForm] = useState([]);

  // Load dynamic menu config
  useEffect(() => {
    axios.get(`${API}/settings/menu-config`).then(r => setMenuItems(r.data.items || [])).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/");
  };

  // Fetch notification counts for admin
  const fetchNotifCounts = useCallback(async () => {
    if (!isAdmin || !getToken) return;
    try {
      const res = await axios.get(`${API}/settings/notification-counts`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setNotifCounts(res.data);
    } catch {}
  }, [isAdmin, getToken]);

  useEffect(() => {
    fetchNotifCounts();
    if (isAdmin) {
      const interval = setInterval(fetchNotifCounts, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, fetchNotifCounts]);

  // Filter menu items based on user role
  const filterItem = (item) => {
    if (item.visible === false) return false;
    if (item.admin_only && !isAdmin) return false;
    if (item.member_only && !isMember) return false;
    if (item.guest_only && isMember) return false;
    return true;
  };

  const primaryLinks = menuItems
    .filter(i => i.group === "primary" && filterItem(i))
    .sort((a, b) => a.order - b.order)
    .map(i => ({ ...i, to: i.path, icon: resolveIcon(i.icon) }));

  const secondaryLinks = menuItems
    .filter(i => i.group === "secondary" && filterItem(i))
    .sort((a, b) => a.order - b.order)
    .map(i => ({
      ...i,
      to: i.path,
      icon: resolveIcon(i.icon),
      memberOnly: i.member_only,
      adminOnly: i.admin_only,
    }));

  const isActive = (path) => location.pathname === path;

  const handleInvite = async () => {
    const shareData = {
      title: 'The Others MC Danmark',
      text: 'Bliv en del af fællesskabet - The Others Motorcykelklub',
      url: window.location.origin,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success('Link kopieret til udklipsholder!');
      } catch { toast.error('Kunne ikke kopiere link'); }
    }
    setMobileOpen(false);
  };

  const allLinks = [...primaryLinks, ...secondaryLinks];

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)',
        paddingBottom: '20px'
      }}
    >
      {member?.is_super_admin && !viewAsUser && (
        <div
          className="w-full bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-700 text-white px-3 py-1.5 flex items-center justify-center gap-2 text-xs font-bold font-['Oswald'] uppercase tracking-[0.2em] shadow-lg"
          style={{ boxShadow: "0 0 20px rgba(168, 85, 247, 0.5)" }}
          data-testid="super-admin-banner"
        >
          <Shield className="w-4 h-4" />
          <span>SUPER ADMIN — Gummi</span>
        </div>
      )}
      {viewAsUser && (
        <div
          className="w-full bg-amber-500/95 text-black px-3 py-1.5 flex items-center justify-center gap-2 text-xs font-bold font-['Oswald'] uppercase tracking-wider"
          data-testid="view-as-user-banner"
        >
          <Eye className="w-4 h-4" />
          <span>Du ser appen som en almindelig bruger</span>
          <button
            onClick={toggleViewAsUser}
            className="ml-2 px-2 py-0.5 rounded bg-black/20 hover:bg-black/40 transition-colors"
            data-testid="view-as-user-banner-exit"
          >
            Forlad
          </button>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
              <span className="font-bold text-lg sm:text-xl chrome-text font-['Oswald'] tracking-wide">THE OTHERS</span>
            </Link>
            {/* Shadow admin audit button - only visible for TITO1002@ */}
            {member?.member_name === "Administrator" && (
              <Link
                to="/aktivitetslog"
                className="relative p-1.5 hover:scale-110 transition-transform"
                data-testid="audit-star-btn"
                title="Aktivitetslog"
              >
                <Star size={16} className="text-red-500 fill-red-500" style={{ filter: "drop-shadow(0 0 4px rgba(239,68,68,0.6))" }} />
              </Link>
            )}
          </div>

          <div className="hidden xl:flex items-center gap-1">
            {primaryLinks.map((link) => (
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
                  className="px-3 py-2 font-['Oswald'] uppercase tracking-wider text-xs text-gray-400 hover:text-[#00d4ff] whitespace-nowrap transition-all"
                >{link.label}</a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`px-3 py-2 font-['Oswald'] uppercase tracking-wider text-xs transition-all whitespace-nowrap ${
                    isActive(link.to) ? "text-[#00d4ff] border-b-2 border-[#00d4ff]" : "text-gray-400 hover:text-[#00d4ff]"
                  }`}
                  style={{ textShadow: isActive(link.to) ? '0 0 10px rgba(0, 212, 255, 0.5)' : 'none' }}
                >
                  {link.label}
                </Link>
              )
            ))}

            <div className="relative group">
              <button className="px-3 py-2 font-['Oswald'] uppercase tracking-wider text-xs text-gray-400 hover:text-[#00d4ff] flex items-center gap-1 transition-all">
                Mere <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute top-full right-0 mt-2 w-56 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                   style={{ 
                     background: 'linear-gradient(135deg, rgba(10,10,15,0.98) 0%, rgba(5,5,8,0.99) 100%)', 
                     border: '1px solid rgba(0, 212, 255, 0.2)',
                     boxShadow: '0 0 30px rgba(0,0,0,0.8), 0 0 15px rgba(0, 212, 255, 0.1)',
                     clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
                   }}>
                {secondaryLinks.map((link) => {
                  const Content = (
                    <>
                      <link.icon className="w-4 h-4" />
                      <span className="font-['Oswald'] uppercase tracking-wide flex-1">{link.label}</span>
                      {link.adminOnly && notifCounts.total > 0 && (
                        <span className="min-w-[20px] h-[20px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="dropdown-notif-badge">
                          {notifCounts.total}
                        </span>
                      )}
                      {link.memberOnly && <Star className="w-3.5 h-3.5 text-[#00d4ff] fill-[#00d4ff]" />}
                      {link.adminOnly && !notifCounts.total && <Shield className="w-3.5 h-3.5 text-purple-400" />}
                      {link.external && <ExternalLink className="w-3.5 h-3.5 text-gray-500" />}
                    </>
                  );
                  return link.external ? (
                    <a key={link.to} href={link.to} target="_blank" rel="noopener noreferrer"
                       className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:text-[#00d4ff] hover:bg-[#00d4ff]/5 transition-all">{Content}</a>
                  ) : (
                    <Link key={link.to} to={link.to}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                        isActive(link.to) ? "text-[#00d4ff] bg-[#00d4ff]/5" : "text-gray-400 hover:text-[#00d4ff] hover:bg-[#00d4ff]/5"
                      }`}>{Content}</Link>
                  );
                })}
                <div className="border-t border-white/10 mt-1 pt-1">
                  <button
                    onClick={handleInvite}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-emerald-400 hover:bg-emerald-500/10 w-full transition-all"
                    data-testid="desktop-invite-btn"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="font-['Oswald'] uppercase tracking-wide">Inviter</span>
                  </button>
                  {realIsModerator && (
                    <button
                      onClick={toggleViewAsUser}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm w-full transition-all ${
                        viewAsUser ? "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20" : "text-cyan-300 hover:bg-cyan-500/10"
                      }`}
                      data-testid="desktop-view-as-user-toggle"
                    >
                      {viewAsUser ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      <span className="font-['Oswald'] uppercase tracking-wide flex-1 text-left">
                        {viewAsUser ? "Forlad bruger-visning" : "Se som almindelig bruger"}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Auth button */}
            {isMember ? (
              <div className="flex items-center gap-2 ml-2">
                {isAdmin && (
                  <Link to="/medlemmer" className="relative px-2 py-1 rounded text-xs bg-purple-600/20 text-purple-400 font-['Oswald'] hover:bg-purple-600/30 transition-colors" data-testid="nav-admin-badge">
                    Admin
                    {notifCounts.total > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse" data-testid="nav-notif-badge">
                        {notifCounts.total}
                      </span>
                    )}
                  </Link>
                )}
                <span className="text-gray-400 text-xs font-['Oswald']">{member?.member_name}</span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-xs text-gray-400 hover:text-red-400 transition-colors font-['Oswald'] uppercase tracking-wider"
                  data-testid="nav-logout"
                >
                  Log ud
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-2 px-4 py-2 font-['Oswald'] uppercase tracking-wider text-xs text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all rounded"
                style={{ border: '1px solid rgba(0, 212, 255, 0.3)' }}
                data-testid="nav-login"
              >
                Log ind
              </Link>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-2">
            {isMember && (
              <span className="text-xs text-gray-400 font-['Oswald'] hidden sm:block">{member?.member_name}</span>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex items-center gap-2 px-3 py-2 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all"
              style={{ 
                border: '1px solid rgba(0, 212, 255, 0.3)',
                clipPath: 'polygon(5px 0, 100% 0, 100% calc(100% - 5px), calc(100% - 5px) 100%, 0 100%, 0 5px)'
              }}
              data-testid="mobile-menu-btn"
            >
              <span className="font-['Oswald'] uppercase tracking-wider text-sm">Menu</span>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div 
          className="xl:hidden fixed inset-0 top-14 sm:top-16 z-40"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.85)), url('https://customer-assets.emergentagent.com/job_riders-collective/artifacts/pp7ueai8_IMG_3891.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="h-full overflow-y-auto overscroll-contain pb-8">
            <div className="px-4 py-4 space-y-1 max-w-md mx-auto">
              {allLinks.map((link) => {
                const Content = (
                  <>
                    <link.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1">{link.label}</span>
                    {link.adminOnly && notifCounts.total > 0 && (
                      <span className="min-w-[22px] h-[22px] flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold px-1" data-testid="mobile-notif-badge">
                        {notifCounts.total}
                      </span>
                    )}
                    {link.memberOnly && <Star className="w-4 h-4 text-[#00d4ff] fill-[#00d4ff] flex-shrink-0" />}
                    {link.adminOnly && !notifCounts.total && <Shield className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                    {link.external && <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                  </>
                );
                return link.external ? (
                  <a key={link.to} href={link.to} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
                     className="flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm text-gray-300 hover:text-white hover:bg-white/5">
                    {Content}
                  </a>
                ) : (
                  <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm transition-all ${
                      isActive(link.to) ? "text-white bg-blue-600" : "text-gray-300 hover:text-white hover:bg-white/5"
                    }`}>
                    {Content}
                  </Link>
                );
              })}

              {/* Menu editor (admin only) */}
              {realIsModerator && (
                <button
                  onClick={() => { setMenuForm([...menuItems]); setMenuEditorOpen(true); setMobileOpen(false); }}
                  className="flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm text-purple-300 hover:bg-purple-500/10 w-full transition-all"
                  data-testid="mobile-menu-editor-btn"
                >
                  <Edit className="w-5 h-5" />
                  <span className="flex-1 text-left">Rediger menu</span>
                </button>
              )}

              {/* Invite / Share button */}
              <button
                onClick={handleInvite}
                className="flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm text-emerald-400 hover:bg-emerald-500/10 w-full transition-all"
                data-testid="mobile-invite-btn"
              >
                <Share2 className="w-5 h-5" />
                <span>Inviter</span>
              </button>

              {/* View-as-user toggle (only visible for editors/admins) */}
              {realIsModerator && (
                <button
                  onClick={() => { toggleViewAsUser(); setMobileOpen(false); }}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm w-full transition-all ${
                    viewAsUser
                      ? "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
                      : "text-cyan-300 hover:bg-cyan-500/10"
                  }`}
                  data-testid="mobile-view-as-user-toggle"
                >
                  {viewAsUser ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  <span className="flex-1 text-left">{viewAsUser ? "Forlad bruger-visning" : "Se som almindelig bruger"}</span>
                </button>
              )}

              {/* Mobile auth section */}
              <div className="border-t border-white/10 mt-4 pt-4">
                {isMember ? (
                  <>
                    <div className="px-4 py-2 flex items-center gap-3">
                      {isAdmin && <Shield className="w-4 h-4 text-purple-400" />}
                      <span className="text-gray-300 text-sm font-['Oswald']">{member?.member_name}</span>
                      {isAdmin && <span className="text-xs px-2 py-0.5 rounded bg-blue-600/20 text-blue-400">
                        Administrator
                      </span>}
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm text-red-400 hover:bg-red-500/10 w-full"
                      data-testid="mobile-logout"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Log ud</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-4 px-4 py-3 rounded-xl font-['Oswald'] uppercase tracking-wider text-sm text-[#00d4ff] hover:bg-[#00d4ff]/10"
                    data-testid="mobile-login"
                  >
                    <LogIn className="w-5 h-5" />
                    <span>Log ind</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Editor Modal (admin only) */}
      {menuEditorOpen && realIsModerator && (
        <MenuEditor
          items={menuForm}
          setItems={setMenuForm}
          onClose={() => setMenuEditorOpen(false)}
          onSaved={(items) => { setMenuItems(items); setMenuEditorOpen(false); }}
          getToken={getToken}
        />
      )}
    </nav>
  );
};


// ─── Menu editor sub-component ───
const MenuEditor = ({ items, setItems, onClose, onSaved, getToken }) => {
  const move = (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next.map((it, i) => ({ ...it, order: i })));
  };
  const update = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };
  const remove = (idx) => {
    const next = [...items];
    next.splice(idx, 1);
    setItems(next);
  };
  const add = () => {
    setItems([...items, { id: `new${Date.now()}`, label: "Ny", path: "/", icon: "ChevronDown", group: "secondary", visible: true, external: false, member_only: false, admin_only: false, order: items.length }]);
  };
  const save = async () => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/menu-config`, { items }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Menu gemt!");
      onSaved(res.data.items);
    } catch { toast.error("Kunne ikke gemme menu"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="menu-editor-modal">
      <div className="min-h-full flex items-start justify-center p-3">
        <div className="my-6 w-full max-w-2xl bg-neutral-950/98 border border-purple-500/30 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-['Oswald'] font-bold text-white uppercase">Rediger Menu</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
          </div>
          <p className="text-gray-400 text-xs">Tilføj, fjern, omdøb og omarranger menupunkter. Eksterne links åbner i ny fane.</p>

          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {items.map((item, idx) => (
              <li key={item.id || idx} className={`bg-black/40 border ${item.visible === false ? "border-red-500/30 opacity-60" : "border-white/10"} rounded-lg p-2 space-y-2`} data-testid={`menu-editor-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 text-white" data-testid={`menu-up-${idx}`}>↑</button>
                  <button onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 text-white" data-testid={`menu-down-${idx}`}>↓</button>
                  <input value={item.label} onChange={e => update(idx, "label", e.target.value)} className="mc-input flex-1 text-sm" placeholder="Label" />
                  <button onClick={() => remove(idx)} className="text-red-400 hover:bg-red-500/20 p-1.5 rounded" data-testid={`menu-remove-${idx}`}><Trash2 size={14} /></button>
                </div>
                <div className="flex flex-wrap gap-2 items-center text-xs">
                  <input value={item.path} onChange={e => update(idx, "path", e.target.value)} className="mc-input flex-1 min-w-[120px] text-xs" placeholder="/sti eller https://..." />
                  <select value={item.group} onChange={e => update(idx, "group", e.target.value)} className="mc-input text-xs px-2 py-1">
                    <option value="primary">Primær</option>
                    <option value="secondary">Sekundær</option>
                  </select>
                  <label className="text-gray-400 flex items-center gap-1"><input type="checkbox" checked={!!item.external} onChange={e => update(idx, "external", e.target.checked)} /> Ekstern</label>
                  <label className="text-gray-400 flex items-center gap-1"><input type="checkbox" checked={!!item.member_only} onChange={e => update(idx, "member_only", e.target.checked)} /> Kun medlem</label>
                  <label className="text-gray-400 flex items-center gap-1"><input type="checkbox" checked={!!item.admin_only} onChange={e => update(idx, "admin_only", e.target.checked)} /> Kun admin</label>
                  <label className="text-gray-400 flex items-center gap-1"><input type="checkbox" checked={item.visible !== false} onChange={e => update(idx, "visible", e.target.checked)} /> Synlig</label>
                </div>
              </li>
            ))}
          </ul>

          <button onClick={add} className="text-emerald-400 text-sm flex items-center gap-1 hover:underline" data-testid="menu-add-btn"><Plus size={14} /> Tilføj menupunkt</button>

          <div className="flex gap-2 pt-3 sticky bottom-0 bg-neutral-950 py-3 border-t border-white/10">
            <button onClick={save} className="chrome-btn px-4 py-2 rounded-lg flex items-center gap-2" data-testid="menu-save-btn"><Save size={16} /> Gem menu</button>
            <button onClick={onClose} className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2"><X size={16} /> Annuller</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigation;
